import React, { useEffect, useMemo, useState } from "react";
import { customerApi, formatApiError, formatBranchMoney, formatBranchHours } from "@/lib/api";
import CustomerNav from "@/components/layout/CustomerNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, User, Sparkles, CalendarPlus, Star, ChevronRight } from "lucide-react";
import { buildIcs, downloadIcs } from "@/lib/ics";

// Feb 2026 iter 173: booking wizard collapsed from FIVE screens
// (Branch / Service / Employee / Date & Time / Confirm) to FOUR:
//   1. Branch          (unchanged, 2-column card grid)
//   2. Service         (grouped by category, "FREE Doctor Consultation"
//                       card pinned first, "Dermatology" category pinned
//                       right beneath — everything else follows below)
//   3. Schedule        (Date → available specialists for that date →
//                       time slots — provider-first flow, so the guest
//                       always sees WHO is free before committing to a
//                       time)
//   4. Confirm         (full summary + Confirm booking button)
// The Back / Continue navigation bar is sticky directly under the
// stepper so it's always visible on scroll.
const STEPS = ["Branch", "Service", "Schedule", "Confirm"];

const PRIORITY_CATEGORY = "Dermatology";
const CONSULTATION_MATCH = /free doctor consultation|doctor consultation|free consultation/i;

export default function BookFlow() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rescheduleFromId = params.get("rescheduleFrom");
  const preselectServiceId = params.get("serviceId");
  const preselectBranchId = params.get("branchId");
  const isReschedule = !!rescheduleFromId;

  const [step, setStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({ slots: [] });

  const [branch, setBranch] = useState(null);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(new Date());
  const [staff, setStaff] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookedAppt, setBookedAppt] = useState(null);
  const [customerNote, setCustomerNote] = useState("");

  useEffect(() => {
    customerApi.get("/portal/branches")
      .then((r) => setBranches(r.data))
      .catch((err) => toast.error(formatApiError(err)));
    customerApi.get("/services", { params: { online_only: true } }).then((r) => setServices(r.data)).catch(() => {});
  }, []);

  // On reschedule: pre-select branch + service once both lists have loaded.
  useEffect(() => {
    if (!isReschedule) return;
    if (preselectBranchId && !branch && branches.length) {
      const b = branches.find((x) => x.id === preselectBranchId);
      if (b) setBranch(b);
    }
    if (preselectServiceId && !service && services.length) {
      const s = services.find((x) => x.id === preselectServiceId);
      if (s) setService(s);
    }
  }, [isReschedule, preselectBranchId, preselectServiceId, branches, services, branch, service]);

  // Reset schedule state when branch/service changes so a stale staff
  // selection can't survive across a service switch.
  useEffect(() => {
    setStaff(null);
    setSlot(null);
    setAvailability({ slots: [] });
  }, [branch, service]);

  const isoDate = useMemo(() => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [date]);

  // Fetch the entire day's availability (no staff filter) whenever the
  // guest is on Step 3 and has picked a Branch + Service + Date. The
  // response groups slots per staff so we can derive the "which
  // specialists can perform this service today" list on the frontend —
  // no separate backend endpoint needed.
  useEffect(() => {
    if (step !== 2 || !branch || !service) return;
    let cancelled = false;
    setAvailability({ loading: true, slots: [] });
    customerApi.get("/availability", {
      params: { branch_id: branch.id, service_id: service.id, date: isoDate },
    })
      .then((r) => { if (!cancelled) setAvailability(r.data || { slots: [] }); })
      .catch((err) => { if (!cancelled) { setAvailability({ slots: [] }); toast.error(formatApiError(err)); } });
    return () => { cancelled = true; };
  }, [step, branch, service, isoDate]);

  // Reset staff/slot whenever the guest changes the date so a specialist
  // available on Monday isn't accidentally kept selected for Tuesday.
  useEffect(() => {
    setStaff(null);
    setSlot(null);
  }, [isoDate]);

  // Group slots per staff and sort by name for the specialist picker.
  // Each group carries the underlying slots array so once a specialist
  // is tapped the time-slot grid renders instantly with no re-fetch.
  const staffToday = useMemo(() => {
    const map = new Map();
    (availability.slots || []).forEach((s) => {
      if (!map.has(s.staff_id)) map.set(s.staff_id, { id: s.staff_id, full_name: s.staff_name, slots: [] });
      map.get(s.staff_id).slots.push(s);
    });
    const list = Array.from(map.values());
    list.forEach((g) => g.slots.sort((a, b) => a.start_iso.localeCompare(b.start_iso)));
    list.sort((a, b) => a.full_name.localeCompare(b.full_name));
    return list;
  }, [availability]);

  // Category ordering for Step 2:
  //   1. "FREE Doctor Consultation"   — synthetic bucket for any
  //      service whose name matches CONSULTATION_MATCH (usually the
  //      complimentary derm consultation).
  //   2. "Dermatology"                — pinned second per requirement.
  //   3. every other category, alphabetically.
  // Services are filtered by branch first so a Legian-only ritual
  // never surfaces on the Ubud picker.
  const servicesByCategory = useMemo(() => {
    if (!branch) return [];
    const eligible = services.filter(
      (s) => !s.branch_ids?.length || s.branch_ids.includes(branch.id),
    );
    const consult = eligible.filter((s) => CONSULTATION_MATCH.test(s.name || ""));
    const rest = eligible.filter((s) => !CONSULTATION_MATCH.test(s.name || ""));
    const buckets = new Map();
    rest.forEach((s) => {
      const cat = (s.category || "Other").trim() || "Other";
      if (!buckets.has(cat)) buckets.set(cat, []);
      buckets.get(cat).push(s);
    });
    const ordered = [];
    if (consult.length) ordered.push({ name: "FREE Doctor Consultation", pinned: true, services: consult });
    if (buckets.has(PRIORITY_CATEGORY)) {
      ordered.push({ name: PRIORITY_CATEGORY, pinned: true, services: buckets.get(PRIORITY_CATEGORY) });
      buckets.delete(PRIORITY_CATEGORY);
    }
    Array.from(buckets.keys()).sort((a, b) => a.localeCompare(b)).forEach((cat) => {
      ordered.push({ name: cat, pinned: false, services: buckets.get(cat) });
    });
    return ordered;
  }, [branch, services]);

  const confirm = async () => {
    if (!slot) return;
    setBusy(true);
    try {
      const res = await customerApi.post("/appointments", {
        branch_id: branch.id,
        service_id: service.id,
        staff_id: slot.staff_id,
        start_iso: slot.start_iso,
        // Guest-authored heads-up for the front desk & clinician. Empty
        // string is fine — server persists it as `appointment.notes`,
        // which the calendar tooltip (DayGrid + WeekStaffGrid) reads on
        // hover AND the AI briefing pipeline includes under
        // `todays_note` for the care team.
        notes: (customerNote || "").trim() || undefined,
      });

      if (isReschedule && rescheduleFromId) {
        try {
          await customerApi.delete(`/appointments/${rescheduleFromId}`, {
            data: {
              reason_label: "Rescheduled by customer",
              note: "Auto-cancelled after customer rebooked.",
            },
          });
        } catch (err) {
          console.warn("Reschedule: failed to cancel original appointment", err);
        }
      }

      toast.success(isReschedule ? "Your visit has been rescheduled." : "Your appointment is booked");
      setBookedAppt(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 && isReschedule) {
        toast.error("That appointment can't be moved online — please WhatsApp us so the front desk can help.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const addToCalendar = () => {
    if (!bookedAppt) return;
    const location = branch
      ? `Cocoon Medical Spa · ${branch.name}${branch.address ? ` (${branch.address})` : ""}`
      : "Cocoon Medical Spa";
    const description = [
      `Service: ${service?.name}`,
      `Duration: ${service?.duration_min} min`,
      `Therapist: ${slot?.staff_name || staff?.full_name || ""}`.trim(),
      branch?.phone ? `Contact: ${branch.phone}` : null,
    ].filter(Boolean).join("\n");
    const ics = buildIcs({
      uid: bookedAppt.id,
      title: `Cocoon · ${service?.name}`,
      description,
      location,
      start: bookedAppt.start_iso,
      end: bookedAppt.end_iso,
    });
    const dt = new Date(bookedAppt.start_iso);
    const stamp = `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}${String(dt.getDate()).padStart(2, "0")}`;
    downloadIcs(`cocoon-${stamp}.ics`, ics);
    toast.success("Calendar file downloaded — open it to add reminders.");
  };

  const canNext =
    (step === 0 && branch) ||
    (step === 1 && service) ||
    (step === 2 && slot) ||
    false;

  return (
    <div>
      <CustomerNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pb-6 sm:pb-10">

        {/* Fixed step + navigation bar. Sits directly under the fixed
            portal tab bar so it's always visible while the guest scrolls
            a long category list or a full month of slots. We use
            `fixed` (not `sticky`) because the app has global
            `overflow: hidden auto` on html/body/.App, which is a
            well-known killer of `position: sticky`. The spacer div below
            reserves the vertical footprint so the "Book your visit" H1
            (and every step's content) never sits behind the fixed bar.

              • Signed-in mobile: portal tabs = 108 px, step-bar height
                ≈ 88 px → total 196 px of pinned chrome. Spacer h≈88.
              • Signed-in desktop (md+): portal tabs = 64 px, step-bar
                height ≈ 76 px → total 140 px. Spacer h≈76.
        */}
        {!bookedAppt && (
          <>
            <div
              className="fixed top-[108px] md:top-16 left-0 right-0 z-30 bg-background/95 backdrop-blur-md border-b border-border/70"
              data-testid="booking-step-header"
            >
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-3">
                <ol className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                  {STEPS.map((s, i) => (
                    <li key={s} className="flex items-center gap-2 text-xs sm:text-sm">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] ${i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{i < step ? <Check className="w-3 h-3" /> : i + 1}</span>
                      <span className={i === step ? "text-foreground" : "text-muted-foreground"}>{s}</span>
                      {i < STEPS.length - 1 && <span className="mx-1 text-muted-foreground hidden sm:inline">·</span>}
                    </li>
                  ))}
                </ol>
                <div className="flex items-center justify-between">
                  {/*
                    Back button — circular Cocoon brown border. Uses an
                    inline hex on the border so we don't have to invent
                    a new Tailwind theme token just for one control. The
                    active state fills the circle in the same brown for
                    press feedback, and disabled dims it 40%.
                  */}
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    data-testid="step-back-btn"
                    className="inline-flex items-center justify-center h-9 min-w-[76px] px-4 rounded-full border-2 text-sm font-medium transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#8B4513]/10 active:bg-[#8B4513]/20"
                    style={{ borderColor: "#8B4513", color: "#8B4513" }}
                  >
                    Back
                  </button>
                  {step < 3 ? (
                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={() => setStep((s) => s + 1)}
                      disabled={!canNext}
                      data-testid="step-next-btn"
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={confirm}
                      disabled={busy || !slot}
                      data-testid="confirm-booking-btn-top"
                    >
                      {busy ? "Booking…" : "Confirm booking"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {/* Spacer to reserve the fixed step-bar's footprint. Extra
                bottom margin gives the "Book your visit" H1 breathing
                room so it never sits behind the fixed nav strip. */}
            <div aria-hidden className="h-[92px] md:h-[80px]" />
          </>
        )}

        {/* Hero — sits BELOW both the fixed portal tabs AND the fixed
            step-bar (thanks to the spacer above) so nothing crops it. */}
        {!bookedAppt && (
          <div className="pt-4 sm:pt-6">
            <p className="tracking-overline text-xs text-muted-foreground">
              {isReschedule ? "RESCHEDULE YOUR VISIT" : "RESERVE A RITUAL"}
            </p>
            <h1 className="font-editorial text-4xl sm:text-5xl mt-3 mb-6 sm:mb-8">
              {isReschedule ? "Pick a new time" : "Book your visit"}
            </h1>
          </div>
        )}

        {/* ─── STEP 1 · Branch ─────────────────────────────────────── */}
        {step === 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="step-branch">
            {branches.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="branches-empty-state">
                No online-booking locations are available right now. Please contact us via WhatsApp and we&apos;ll book you in.
              </div>
            )}
            {branches.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBranch(b)}
                data-testid={`select-branch-${b.name.toLowerCase()}`}
                className={`text-left px-3 py-2.5 sm:p-4 rounded-xl border transition-colors duration-200 min-h-[80px] sm:min-h-[120px] active:scale-[0.99] ${branch?.id === b.id ? "border-primary bg-primary/5" : "border-border bg-white hover:border-primary/40"}`}
              >
                <p className="font-editorial text-base sm:text-xl leading-tight truncate">{b.name}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 leading-snug line-clamp-1">{b.address}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 leading-snug">{formatBranchHours(b)}</p>
              </button>
            ))}
          </div>
        )}

        {/* ─── STEP 2 · Service ────────────────────────────────────── */}
        {step === 1 && (
          <div data-testid="step-service">
            {servicesByCategory.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="services-empty-state">
                No services are available at this location. Please try another branch.
              </div>
            )}

            {servicesByCategory.length > 0 && (
              <Accordion
                type="single"
                collapsible
                defaultValue={servicesByCategory[0]?.name}
                data-testid="service-accordion"
                className="space-y-1.5"
              >
                {servicesByCategory.map((group) => {
                  const slug = group.name.toLowerCase().replace(/\s+/g, "-");
                  return (
                    <AccordionItem
                      key={group.name}
                      value={group.name}
                      className="border rounded-xl overflow-hidden bg-white data-[state=open]:border-primary/40"
                      data-testid={`service-category-${slug}`}
                    >
                      <AccordionTrigger
                        className="px-3 py-2.5 hover:no-underline"
                        data-testid={`service-category-trigger-${slug}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={
                            "tracking-overline text-[11px] uppercase truncate " +
                            (group.pinned ? "text-primary" : "text-foreground")
                          }>
                            {group.name}
                          </span>
                          <Badge variant="outline" className="rounded-full text-[10px] shrink-0">
                            {group.services.length}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-1 pb-2 px-2">
                        <div className="grid grid-cols-2 gap-2.5">
                          {group.services.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => setService(s)}
                              data-testid={`select-service-${s.id}`}
                              className={
                                "text-left px-2.5 py-2 rounded-lg border transition-colors duration-200 min-h-[60px] active:scale-[0.99] " +
                                (service?.id === s.id
                                  ? "border-primary bg-primary/5"
                                  : "border-border bg-white hover:border-primary/40")
                              }
                            >
                              <p className="font-editorial text-sm sm:text-base leading-tight line-clamp-2">
                                {s.name}
                              </p>
                              <p className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                                {formatBranchMoney(branch, s.price)}
                              </p>
                            </button>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>
        )}

        {/* ─── STEP 3 · Schedule (Date → Specialist → Time) ────────── */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-schedule">
            <div>
              <p className="tracking-overline text-[11px] text-muted-foreground mb-2">PICK A DATE</p>
              <Card className="rounded-2xl">
                <CardContent className="p-2 sm:p-4 flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }}
                    className="rounded-md"
                    data-testid="schedule-calendar"
                  />
                </CardContent>
              </Card>
            </div>

            <div data-testid="schedule-specialists">
              <p className="tracking-overline text-[11px] text-muted-foreground mb-2">
                CARE TEAM AVAILABLE · {isoDate}
              </p>
              {availability.loading ? (
                <p className="text-sm text-muted-foreground">Checking availability…</p>
              ) : availability.closed ? (
                <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="schedule-branch-closed">
                  {availability.reason || "Branch is closed on this date. Please pick another day."}
                </div>
              ) : staffToday.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="schedule-no-staff">
                  No specialists are available for {service?.name} on this date. Please pick another day.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  {staffToday.map((s) => {
                    const active = staff?.id === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => { setStaff(s); setSlot(null); }}
                        data-testid={`select-staff-${s.id}`}
                        className={`text-left p-4 rounded-2xl border transition-colors duration-200 flex items-center gap-3 active:scale-[0.99] ${active ? "border-primary bg-primary/5" : "border-border bg-white hover:border-primary/40"}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-primary/15 text-primary" : "bg-secondary text-foreground"}`}>
                          <User className="w-4.5 h-4.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-editorial text-lg leading-tight truncate">{s.full_name}</p>
                          <p className="text-[11px] text-muted-foreground">{s.slots.length} slot{s.slots.length === 1 ? "" : "s"} available</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {staff && (
              <div data-testid="schedule-slots">
                <p className="tracking-overline text-[11px] text-muted-foreground mb-2">
                  TIMES WITH {staff.full_name.toUpperCase()}
                </p>
                {staff.slots.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No open times with this specialist on {isoDate}.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {staff.slots.map((s, idx) => {
                      const active = slot && slot.staff_id === s.staff_id && slot.start_iso === s.start_iso;
                      return (
                        <button
                          key={`${s.staff_id}-${s.start_iso}`}
                          type="button"
                          onClick={() => setSlot(s)}
                          data-testid={`slot-${idx}`}
                          className={`px-2 py-2.5 rounded-xl border text-sm font-medium transition-colors duration-200 ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-white hover:border-primary/40"}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 4 · Confirm ─────────────────────────────────────── */}
        {step === 3 && !bookedAppt && (
          <Card className="rounded-2xl" data-testid="step-confirm">
            <CardContent className="p-5 sm:p-8 space-y-4 sm:space-y-5">
              <div>
                <p className="tracking-overline text-[11px] text-muted-foreground">SANCTUARY</p>
                <p className="font-editorial text-2xl sm:text-3xl mt-1" data-testid="confirm-branch-name">{branch?.name}</p>
                <p className="text-xs text-muted-foreground">{branch?.address}</p>
              </div>
              <div>
                <p className="tracking-overline text-[11px] text-muted-foreground">RITUAL</p>
                <p className="font-editorial text-xl sm:text-2xl mt-1" data-testid="confirm-service-name">{service?.name}</p>
                <p className="text-xs text-muted-foreground">
                  {service?.duration_min} min · <span data-testid="confirm-service-price">{formatBranchMoney(branch, service?.price)}</span>
                </p>
              </div>
              <div>
                <p className="tracking-overline text-[11px] text-muted-foreground">CARE TEAM</p>
                <p className="text-base sm:text-lg mt-1" data-testid="confirm-staff-name">{slot?.staff_name || staff?.full_name}</p>
              </div>
              <div>
                <p className="tracking-overline text-[11px] text-muted-foreground">DATE &amp; TIME</p>
                <p className="text-base sm:text-lg mt-1" data-testid="confirm-datetime">{isoDate} · {slot?.label}</p>
              </div>
              {/*
                Special Notes — free-text heads-up that lands on
                `appointment.notes`. Read by:
                  • DayGrid.jsx & WeekStaffGrid.jsx    (hover tooltip)
                  • routes/staff_briefing.py           (`todays_note`
                    field surfaced under AI briefing "Today's plan")
                So a single string here reaches the reception desk AND
                the treating clinician's AI briefing.
              */}
              <div>
                <Label htmlFor="customer-note" className="tracking-overline text-[11px] text-muted-foreground">
                  SPECIAL NOTES
                </Label>
                <Textarea
                  id="customer-note"
                  value={customerNote}
                  onChange={(e) => setCustomerNote(e.target.value)}
                  placeholder="Is there any special notes for our team before your arrival?"
                  className="mt-1.5 min-h-[96px]"
                  rows={3}
                  maxLength={800}
                  data-testid="customer-note-textarea"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Our reception &amp; care team will see this note when they check you in.
                </p>
              </div>
              <Button
                className="rounded-full w-full sm:w-auto mt-4"
                onClick={confirm}
                disabled={busy}
                data-testid="confirm-booking-btn"
              >
                {busy ? "Booking…" : "Confirm booking"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ─── Booked confirmation ─────────────────────────────────── */}
        {step === 3 && bookedAppt && (
          <Card className="rounded-2xl border-primary/30 bg-primary/5" data-testid="step-booked">
            <CardContent className="p-6 sm:p-10 space-y-5 sm:space-y-6 text-center sm:text-left">
              <div className="mx-auto sm:mx-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                <Check className="w-6 h-6" />
              </div>
              <div>
                <p className="tracking-overline text-[11px] text-muted-foreground">
                  {isReschedule ? "RESCHEDULED" : "YOU'RE BOOKED"}
                </p>
                <h2 className="font-editorial text-3xl sm:text-4xl mt-2">See you at Cocoon Medical Spa {branch?.name}.</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-lg">
                  {service?.name} · {branch?.name} · {isoDate} · {slot?.label}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-2">
                <Button
                  className="rounded-full"
                  onClick={addToCalendar}
                  data-testid="booked-add-to-calendar-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to my calendar
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => nav("/portal/appointments")}
                  data-testid="booked-view-visits-btn"
                >
                  View my visits
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
